/**
 * AuthGuard — protects all admin routes.
 * 
 * 1. Not authenticated → show Login page
 * 2. Authenticated but not staff → show Unauthorized page
 * 3. Authenticated + staff → render children
 * 
 * Auth callback route is always allowed through.
 */
import { type ReactNode } from 'react'
import { useAuth } from '@mzanzi/auth'
import { adminAuthOptions } from '@/auth'
import { LoginPage } from '@/pages/Login'
import { UnauthorizedPage } from '@/pages/Unauthorized'

interface AuthGuardProps {
  children: ReactNode
}

export function AuthGuard({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading, user } = useAuth(adminAuthOptions)

  // Allow auth callback through without checking
  if (typeof window !== 'undefined' && window.location.pathname === '/auth/callback') {
    return children
  }

  // Still loading MSAL state
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground animate-pulse">Authenticating...</p>
        </div>
      </div>
    )
  }

  // Not authenticated → login
  if (!isAuthenticated) {
    return <LoginPage />
  }

  // Authenticated but not staff → access denied
  if (user && !user.isAuthorized) {
    return <UnauthorizedPage />
  }

  // Authenticated + authorized → render app
  return children
}
